import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  DatePicker,
  Button,
  Space,
  message,
  Row,
  Col,
} from 'antd';
import dayjs from 'dayjs';
import api from '../../services/api';

interface LogCommunicationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customerId?: number;
  customerPhone?: string;
  customerEmail?: string;
}

type DirectionType = 'inbound' | 'outbound';
type ChannelType = 'phone' | 'sms' | 'email' | 'in_person' | 'other';

const LogCommunicationModal: React.FC<LogCommunicationModalProps> = ({
  open,
  onClose,
  onSuccess,
  customerId,
  customerPhone,
  customerEmail,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [channel, setChannel] = useState<ChannelType>('phone');

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const payload = {
        customerId: customerId || values.customerId || undefined,
        direction: values.direction as DirectionType,
        channel: values.channel as ChannelType,
        source: values.source || undefined,
        phoneNumber: values.phoneNumber || undefined,
        emailAddress: values.emailAddress || undefined,
        subject: values.subject || undefined,
        body: values.body || undefined,
        durationSeconds: values.durationSeconds ? parseInt(values.durationSeconds, 10) : undefined,
        notes: values.notes || undefined,
        loggedAt: values.loggedAt ? values.loggedAt.toISOString() : undefined,
      };

      await api.post('/communications/log', payload);

      message.success('Communication logged successfully');
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error logging communication:', error);
      message.error('Failed to log communication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Log Communication"
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          direction: 'outbound',
          channel: 'phone',
          loggedAt: dayjs(),
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="direction"
              label="Direction"
              rules={[{ required: true, message: 'Please select a direction' }]}
            >
              <Radio.Group>
                <Radio value="inbound">Inbound</Radio>
                <Radio value="outbound">Outbound</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="channel"
              label="Channel"
              rules={[{ required: true, message: 'Please select a channel' }]}
            >
              <Select
                onChange={(value) => setChannel(value as ChannelType)}
                options={[
                  { label: 'Phone Call', value: 'phone' },
                  { label: 'SMS', value: 'sms' },
                  { label: 'Email', value: 'email' },
                  { label: 'In-Person', value: 'in_person' },
                  { label: 'Other', value: 'other' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="phoneNumber"
              label="Phone Number"
              initialValue={customerPhone}
            >
              <Input placeholder="(555) 123-4567" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="emailAddress"
              label="Email Address"
              initialValue={customerEmail}
            >
              <Input type="email" placeholder="customer@example.com" />
            </Form.Item>
          </Col>
        </Row>

        {channel === 'phone' && (
          <Form.Item
            name="durationSeconds"
            label="Duration (seconds)"
          >
            <Input type="number" placeholder="0" />
          </Form.Item>
        )}

        {(channel === 'email' || channel === 'other') && (
          <Form.Item
            name="subject"
            label="Subject"
          >
            <Input placeholder="Subject line" />
          </Form.Item>
        )}

        <Form.Item
          name="body"
          label="Message/Notes"
        >
          <Input.TextArea rows={4} placeholder="Communication details" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="loggedAt"
              label="Date & Time"
            >
              <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="source"
              label="Source System"
            >
              <Select
                allowClear
                placeholder="Select source (optional)"
                options={[
                  { label: 'Twilio (SMS)', value: 'twilio' },
                  { label: 'Email System', value: 'email_system' },
                  { label: 'Manual', value: 'manual' },
                  { label: 'Grasshopper', value: 'grasshopper' },
                  { label: 'System', value: 'system' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label="Additional Notes"
        >
          <Input.TextArea rows={2} placeholder="Any additional notes about this communication" />
        </Form.Item>

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Log Communication
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default LogCommunicationModal;
